import React from 'react';
import { Document, Page, View, Text, Image } from '@react-pdf/renderer';

import { styles } from './styles';
import {
  TableRow,
  CompactTableCell,
  ProductImage,
  PDFBarcode,
} from './PDFComponents';
import {
  formatSampleTypeDisplay,
  getBorderColor,
  formatDateUTC,
  selectLogo,
} from './utils';
import { getImageUrlForPDF } from '../utils/imageUtils';

import { SampleTagResponse } from '../types';

const FULL_PAGE_STYLES = {
  fontSize: {
    header: 22,
    infoRow: 14,
    tableHeader: 11,
    tableContent: 14,
    barcode: 14,
    swatch: 14,
    swatchNote: 10,
  },
  spacing: {
    padding: 2,
    margin: 10,
    marginSmall: 6,
  },
  dimensions: {
    container: { width: '8.5in', height: '11in', padding: 20, border: 30 },
    productImage: { width: 160, height: 240, marginBottom: 10 },
    barcode: { width: 220, height: 60 },
    swatch: { height: 120, border: '2px' },
    spacer: { height: 200 },
  },
};

function FullPageInfoRow({ label, value }: { label: string; value: string }) {
  return (
    <View
      style={[
        styles.infoRow,
        { paddingVertical: FULL_PAGE_STYLES.spacing.padding },
      ]}
    >
      <Text
        style={[styles.label, { fontSize: FULL_PAGE_STYLES.fontSize.infoRow }]}
      >
        {label}:
      </Text>
      <Text
        style={[
          styles.labelData,
          { fontSize: FULL_PAGE_STYLES.fontSize.infoRow },
        ]}
      >
        {value}
      </Text>
    </View>
  );
}

function getFullPageLogoDimensions(division?: string) {
  const normalizedDivision = division?.toLowerCase().trim();
  if (normalizedDivision === "kid's") return { width: 200, height: 30 };
  if (normalizedDivision === "men's") return { width: 220, height: 30 };
  return { width: 340, height: 32 };
}

export function FirstSamplePDF({ tagData }: { tagData: SampleTagResponse }) {
  const sampleType = tagData?.type_of_sample_tag;
  const styleNumber = tagData?.style_number;
  const developmentType = tagData?.type_of_development;

  const productImageUrl = getImageUrlForPDF(
    tagData?.product_image,
    tagData?.productImageDataUrl,
  );

  // Derived values
  const borderColor = getBorderColor(sampleType, developmentType);
  const containerStyle = {
    ...FULL_PAGE_STYLES.dimensions.container,
    backgroundColor: '#ffffff',
    border: `${FULL_PAGE_STYLES.dimensions.container.border}px solid ${borderColor}`,
  };
  const barcodeValue = tagData?.sample_tag_lpn || tagData?.id;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={containerStyle}>
          {/* Main Content: Two Column Layout */}
          <View
            style={[
              styles.mainContent,
              { marginBottom: FULL_PAGE_STYLES.spacing.margin },
            ]}
          >
            {/* Left Column - All Content */}
            <View style={styles.leftColumn}>
              {/* Logo and Date Header */}
              <View style={styles.logoDateHeader}>
                <Image
                  src={selectLogo(tagData?.division)}
                  style={getFullPageLogoDimensions(tagData?.division)}
                />
              </View>
              {/* Horizontal Line */}
              <View
                style={[
                  styles.horizontalLine,
                  { marginBottom: FULL_PAGE_STYLES.spacing.marginSmall },
                ]}
              />

              {/* Sample Type Header */}
              <Text
                style={[
                  styles.sampleTypeHeader,
                  { fontSize: FULL_PAGE_STYLES.fontSize.header },
                ]}
              >
                {formatSampleTypeDisplay(sampleType).toUpperCase()}
              </Text>

              {/* Product Details */}
              <FullPageInfoRow
                label="Vendor"
                value={
                  tagData?.company_name || tagData?.vendor_legal_name || '-'
                }
              />
              <FullPageInfoRow label="Style #" value={styleNumber || '-'} />
              <FullPageInfoRow
                label="CC Lead Time"
                value={`${tagData?.custom_cut_lead_time || '-'} weeks (${formatDateUTC(tagData?.cc_lead_time_date) || '-'})`}
              />
              <FullPageInfoRow label="COO" value={tagData?.coo || '-'} />
              <FullPageInfoRow
                label="Shipment Mode"
                value={tagData?.shipment_mode || '-'}
              />
              <FullPageInfoRow
                label="FN Exclusive"
                value={tagData?.fn_exclusive ? 'Yes' : 'No'}
              />
              <FullPageInfoRow
                label="Fit Sample"
                value={tagData?.fit_sample ? 'Yes' : 'No'}
              />

              {/* Cost and MOQ Table */}
              <View
                style={{
                  marginTop: FULL_PAGE_STYLES.spacing.margin,
                  marginBottom: FULL_PAGE_STYLES.spacing.margin,
                }}
              >
                <View style={styles.tableRow}>
                  <CompactTableCell
                    width="25%"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableHeader,
                      fontWeight: 'normal',
                    }}
                  >
                    {' '}
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    textAlign="center"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableHeader,
                      fontWeight: 'normal',
                    }}
                  >
                    Cost
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableHeader,
                      fontWeight: 'normal',
                    }}
                  >
                    MOQ
                  </CompactTableCell>
                </View>

                <TableRow>
                  <CompactTableCell
                    width="25%"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                      fontWeight: 'normal',
                    }}
                  >
                    JR
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    textAlign="center"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                    }}
                  >
                    ${tagData?.junior_cost}
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                    }}
                  >
                    {tagData?.junior_moq || '-'}
                  </CompactTableCell>
                </TableRow>

                <TableRow isLast>
                  <CompactTableCell
                    width="25%"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                      fontWeight: 'normal',
                    }}
                  >
                    Plus
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    textAlign="center"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                    }}
                  >
                    {tagData?.plus_cost ? `$${tagData.plus_cost}` : '-'}
                  </CompactTableCell>
                  <CompactTableCell
                    width="37.5%"
                    hasBorder={false}
                    textAlign="center"
                    textStyle={{
                      fontSize: FULL_PAGE_STYLES.fontSize.tableContent,
                    }}
                  >
                    {tagData?.plus_moq || '-'}
                  </CompactTableCell>
                </TableRow>
              </View>

              {/* Fabric Code & Name - Wrapping Row */}
              <View
                style={[
                  {
                    paddingVertical: FULL_PAGE_STYLES.spacing.padding,
                    flexDirection: 'row',
                    alignItems: 'flex-start',
                  },
                ]}
              >
                <Text
                  style={[
                    styles.label,
                    {
                      flexShrink: 0,
                      fontSize: FULL_PAGE_STYLES.fontSize.infoRow,
                    },
                  ]}
                >
                  Fabric Code & Name:
                </Text>
                <Text
                  style={[
                    styles.labelData,
                    {
                      marginLeft: 2,
                      flex: 1,
                      flexWrap: 'wrap',
                      fontSize: FULL_PAGE_STYLES.fontSize.infoRow,
                    },
                  ]}
                >
                  {tagData?.self?.fabric_code_name || '-'}
                </Text>
              </View>
              <FullPageInfoRow
                label="Fabric Content & Weight"
                value={tagData?.self?.fabric_weight?.toString() || '-'}
              />
            </View>

            {/* Right Column - Photo and Barcode */}
            <View style={styles.rightColumn}>
              <ProductImage
                imageUrl={productImageUrl}
                width={FULL_PAGE_STYLES.dimensions.productImage.width}
                height={FULL_PAGE_STYLES.dimensions.productImage.height}
                marginBottom={
                  FULL_PAGE_STYLES.dimensions.productImage.marginBottom
                }
                objectFit="cover"
              />
              <PDFBarcode
                value={barcodeValue}
                width={FULL_PAGE_STYLES.dimensions.barcode.width}
                height={FULL_PAGE_STYLES.dimensions.barcode.height}
                fontSize={FULL_PAGE_STYLES.fontSize.barcode}
              />
            </View>
          </View>

          {/* Fabric Content & Weight Table - Full Width */}
          <View>
            {/* Table Header */}
            <View style={styles.tableRow}>
              <View
                style={{
                  width: '18%',
                  padding: 2,
                  borderRightWidth: 0.5,
                  borderRightColor: '#ffffff',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: 'normal',
                    paddingVertical: 2,
                    textAlign: 'left',
                  }}
                >
                  Type
                </Text>
              </View>
              <View
                style={{
                  width: '62%',
                  padding: 2,
                  borderRightWidth: 0.5,
                  borderRightColor: '#ffffff',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: 'normal',
                    paddingVertical: 2,
                    textAlign: 'left',
                  }}
                >
                  Content
                </Text>
              </View>
              <View
                style={{
                  width: '20%',
                  padding: 2,
                  borderRightWidth: 0,
                  borderRightColor: '#ffffff',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                }}
              >
                <Text
                  style={{
                    fontSize: 11,
                    fontWeight: 'normal',
                    paddingVertical: 2,
                    textAlign: 'right',
                  }}
                >
                  Weight (GSM)
                </Text>
              </View>
            </View>

            {/* Self Row Only */}
            <TableRow isLast>
              <View
                style={{
                  width: '18%',
                  padding: 2,
                  borderRightWidth: 0.5,
                  borderRightColor: '#ffffff',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 'bold',
                    paddingVertical: 2,
                    textAlign: 'left',
                  }}
                >
                  Self
                </Text>
              </View>
              <View
                style={{
                  width: '62%',
                  padding: 2,
                  borderRightWidth: 0.5,
                  borderRightColor: '#ffffff',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 'normal',
                    paddingVertical: 2,
                    textAlign: 'left',
                  }}
                >
                  {tagData?.self?.content
                    ?.map(
                      (fiber: { fiber: string; percent_of_fibers: number }) =>
                        `${fiber.fiber} ${fiber.percent_of_fibers}%`,
                    )
                    .join(' ')}
                </Text>
              </View>
              <View
                style={{
                  width: '20%',
                  padding: 2,
                  borderRightWidth: 0,
                  borderRightColor: '#ffffff',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                }}
              >
                <Text
                  style={{
                    fontSize: 14,
                    fontWeight: 'normal',
                    paddingVertical: 2,
                    textAlign: 'right',
                  }}
                >
                  {tagData?.self?.fabric_weight}
                </Text>
              </View>
            </TableRow>
          </View>

          {/* Bottom Section: Color Swatches and FN Internal */}
          <View style={{ flexDirection: 'row', marginTop: 10 }}>
            {/* Color Swatches Section */}
            <View style={{ flex: 1, marginRight: 10 }}>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 'semibold',
                  marginBottom: 10,
                }}
              >
                Color Swatches
              </Text>

              {/* Grid of 4 swatch boxes */}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap' }}>
                {['swatch1', 'swatch2', 'swatch3', 'swatch4'].map(
                  (swatchId) => (
                    <View
                      key={swatchId}
                      style={{
                        width: '48%',
                        height: 120,
                        margin: '1%',
                        border: '2px dashed #9E9E9E',
                        backgroundColor: '#ffffff',
                      }}
                    />
                  ),
                )}
              </View>

              <Text style={{ fontSize: 10, marginTop: 10 }}>
                Add additional swatches to back of card
              </Text>
            </View>

            {/* Vertical divider line */}
            <View style={[styles.verticalDivider, { marginHorizontal: 20 }]} />

            {/* FN Internal Section */}
            <View style={{ flex: 1, marginLeft: 6 }}>
              <Text
                style={[
                  styles.internalHeader,
                  { fontSize: 14, marginBottom: 10 },
                ]}
              >
                FN INTERNAL ONLY
              </Text>
              <Text
                style={{
                  fontSize: 14,
                  fontWeight: 'semibold',
                  marginBottom: 20,
                }}
              >
                Colors & Units
              </Text>

              {/* Space for content */}
              <View style={{ height: 200 }} />

              {/* Horizontal line above Drop Off Date */}
              <View
                style={{
                  borderBottomWidth: 1,
                  borderBottomColor: '#000',
                  marginBottom: 10,
                  width: '100%',
                }}
              />
              <Text style={{ fontSize: 14, fontWeight: 'semibold' }}>
                Drop Off Date:
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
}
